import { CommonModule } from '@angular/common';
import { Component, ChangeDetectorRef } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { Subscription, tap, catchError, finalize } from 'rxjs';
import { RevokePermissionsComponent } from '../../../../../../views/pages/user-management/account/roles/revoke-permissions/revoke-permissions.component';
import { Logger, ErrorOrError, handleApiErrors } from '../../../../../core';
import { UserContextService } from '../../../../auth-management';
import { UserManagementPermissions, UserManagementCollections } from '../../../../common';
import { AssignRevokePermissionsComponent } from '../../../../permission-management';
import { SpecializedRolesListApiResponse, SpecializedRolesItem } from '../../dtos';
import { SpecializedRolesService } from '../../services';
import { CreateUpdateSpecializedRolesComponent } from '../create-update-specialized-roles';
import { SharedTableComponent, BaseComponent, ToastService } from '../../../../../../trash/shared/components';
import { SahredConstants } from '../../../../../../trash/shared/constants/Values';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-specialized-roles-list',
  standalone: true,
  imports: [SkeletonModule, CommonModule, RouterModule, ButtonModule, MenuModule, SharedTableComponent, TranslateModule],
  templateUrl: './specialized-roles-list.component.html',
  styleUrl: './specialized-roles-list.component.scss',
  providers: [DialogService]
})
export class SpecializedRolesListComponent extends BaseComponent<any> {
  private subscriptions: Subscription[] = [];

  specializedRolesRes: SpecializedRolesListApiResponse | null = null;
  totalItemCount: number = 0;
  isLoadingItems: boolean = false;
  isLoadingFilter: boolean = false;
  searchTerm: string | null = null;

  items = {
    headers: ['GENERAL.NAME', 'GENERAL.ACTIONS'],
    data: [] as SpecializedRolesItem[],
    dataKeys: ['englishName', 'action']
  };

  viewItem: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsSpecializedRoles.Read);
  addItem: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsSpecializedRoles.Create);
  editItem: boolean = UserContextService.hasPermission(UserManagementPermissions.AccountsSpecializedRoles.Update);
  AssignPermissions: boolean = true;
  RevokePermissions: boolean = true;

  SpecializedRolesItemsList: any = [];

  constructor(
    private specializedRolesService: SpecializedRolesService,
    private toastService: ToastService,
    private dialog: DialogService,
    private translate: TranslateService
  ) {
    super();
  }

  override ngOnInit(): void {
    this.SpecializedRolesItemsList = [
      {
        label: this.translate.instant('ACTIONS.EDIT'),
        icon: 'pi pi-pen-to-square',
        command: (event: any) => {
          this.openSpecializedRolesDialog(event, true);
        },
        isView: this.editItem
      },
      {
        label: this.translate.instant('ACTIONS.VIEW'),
        icon: 'pi pi-eye',
        command: (event: any) => {
          this.openSpecializedRolesDialog(event, false);
        },
        isView: this.viewItem
      },
      {
        label: `${this.translate.instant('GENERAL.ASSIGN')}/${this.translate.instant('GENERAL.REVOKE')} ${this.translate.instant('GENERAL.PERMISSIONS')}`,
        icon: 'pi pi-plus',
        command: (event: any) => {
          Logger.debug('event', event);
          this.assignPermissions(event);
        },
        isView: this.AssignPermissions
      }
      // {
      //     label: 'Revoke Permissions',
      //     icon: 'pi pi-replay',
      //     command: (event: any) => {
      //         Logger.debug('event', event);
      //         this.revokePermissions(event);
      //     },
      //     isView: this.RevokePermissions
      // }
    ];
    this.list(true);
  }

  // Start Get Listing Functions
  async list(filter?: boolean) {
    if (this.HandelRenderingDataMessage) {
      this.HandelRenderingDataMessage = 'GENERAL.LOADING';
    }

    filter ? (this.isLoadingFilter = true) : (this.isLoadingItems = true);
    let RolesSubscription: Subscription = this.specializedRolesService
      .getAll(this.request)
      .pipe(
        tap((res: SpecializedRolesListApiResponse) => this.processListResponse(res)),
        catchError((errors: ErrorOrError[]) => handleApiErrors(errors, this.toastService)),
        finalize(() => this.finalizeListRequest())
      )
      .subscribe();

    this.subscriptions.push(RolesSubscription);
  }
  private processListResponse(response: SpecializedRolesListApiResponse): void {
    if (response) {
      this.specializedRolesRes = response;
      this.totalItemCount = response.paginationMetadata.totalItemCount ?? 0;
      this.items.data = this.specializedRolesRes.items;
      if (this.specializedRolesRes.items.length == 0) {
        this.HandelRenderingDataMessage = 'GENERAL.NO_DATA_FOUND';
      }
      this.cdr.markForCheck();
    } else {
      this.HandelRenderingDataMessage = 'GENERAL.NO_DATA_FOUND';
    }
  }
  private finalizeListRequest(): void {
    this.isLoadingItems = false;
    this.isLoadingFilter = false;
  }
  //  End Get Listing Functions

  openSpecializedRolesDialog(item?: SpecializedRolesItem | null, visible?: boolean) {
    let headerTitle: string;
    if (visible == false) {
      headerTitle = this.translate.instant('USER_MANAGEMENT.SPECIALIZED_ROLE_DETAILS');
    } else {
      headerTitle = `${item ? this.translate.instant('ACTIONS.EDIT') : this.translate.instant('GENERAL.CREATE')} ${this.translate.instant('USER_MANAGEMENT.SPECIALIZED_ROLE')}`;
    }
    const ref: any = this.dialog.open(CreateUpdateSpecializedRolesComponent, {
      data: {
        item: item,
        visible: visible
      },
      header: headerTitle,
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose.subscribe((isSuccess: boolean) => {
      if (isSuccess) {
        setTimeout(async () => {
          await this.list();
        }, 1000);
      }
    });
  }

  private assignPermissions(item: any): void {
    const ref = this.dialog.open(AssignRevokePermissionsComponent, {
      data: {
        item: item,
        type: 'assign',
        visible: true,
        endPoint: UserManagementCollections.AccountsSpecializedRolesPermissions(
          UserContextService.getUserContext()?.accountId || '',
          item.id
        )
      },
      header: `${this.translate.instant('GENERAL.ASSIGN')} ${this.translate.instant('GENERAL.PERMISSIONS')}`,
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose?.subscribe((item?: any) => {
      if (item) {
        this.resetParams();
        this.list();
      }
    });
  }
  private revokePermissions(item: any): void {
    const ref = this.dialog.open(RevokePermissionsComponent, {
      data: {
        item: item,
        type: 'revoke',
        visible: true
      },
      header: `${this.translate.instant('GENERAL.REVOKE')} ${this.translate.instant('GENERAL.PERMISSIONS')}`,
      styleClass: 'responsive-dialog-width'
    });
    ref?.onClose?.subscribe((item?: any) => {
      if (item) {
        this.resetParams();
        this.list();
      }
    });
  }

  resetParams(): void {
    this.page = 1;
    this.request.page = SahredConstants.INITIAL_PAGE_INDEX;
    this.request.pageSize = SahredConstants.INITIAL_PAGE_SIZE;
    this.searchTerm = null;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
